import type {CommitsProps} from '@github-ui/commits/shared/Commits'
import type {ConversationComment} from '../../../page-data/payloads/conversation-comments'
import {CommentsSort} from '../use-comments-sort-state'
import {issueCommentItemKey} from './pending-comment-item-keys'

type CommitGroup = CommitsProps['commitGroups'][number]
type Commit = CommitGroup['commits'][number]

export type ConversationConnection = 'above' | 'below'
export type ConnectedConversationBase = {key: string; connection?: ConversationConnection}

type ConnectedConversationComment = ConversationComment & ConnectedConversationBase
export type ConversationCommitGroup = ConnectedConversationBase & {
  type: 'commits'
  commitGroups: CommitGroup[]
  visibleCommitOids: string[]
}

function commentItemKey(comment: ConversationComment) {
  return comment.type === 'issue' ? issueCommentItemKey(comment, `issue-${comment.id}`) : `reviewsummary-${comment.id}`
}

function commitTimestamp(commit: Commit) {
  const timestamp = Date.parse(commit.committedDate ?? commit.authoredDate ?? '')
  return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp
}

/** A commit at the same time as a comment follows that comment in chronological order. */
export function interleaveCommitGroups(
  comments: ConversationComment[],
  commitGroups: CommitGroup[],
  sort: CommentsSort,
): Array<ConnectedConversationComment | ConversationCommitGroup> {
  // Final slots for each type of rendered item
  const slots: CommitGroup[][] = Array.from({length: comments.length + 1}, () => [])

  // All the commits flattened and sorted chronologically
  const commits = commitGroups
    .flatMap(group => group.commits.map(commit => ({commit, title: group.title})))
    .toSorted((a, b) => commitTimestamp(a.commit) - commitTimestamp(b.commit))

  // The set of commit OIDs that should be visible, either first or last
  const visibleCommitOids = new Set([commits[0]?.commit.oid, commits.at(-1)?.commit.oid])
  if (sort === CommentsSort.NewestFirst) {
    commits.reverse()
  }

  const chronologicalComments = sort === CommentsSort.NewestFirst ? comments.toReversed() : comments

  // Place each commit into the appropriate slot based on its timestamp.
  for (const {commit, title} of commits) {
    const timestamp = commitTimestamp(commit)
    let start = 0
    let end = chronologicalComments.length

    // Find the slot index where this commit should be placed.
    while (start < end) {
      const middle = Math.floor((start + end) / 2)
      const comment = chronologicalComments[middle]
      if (comment && Date.parse(comment.createdAt) <= timestamp) {
        start = middle + 1
      } else {
        end = middle
      }
    }

    // Determine the slot for this commit based on the calculated index.
    const slot = slots[sort === CommentsSort.NewestFirst ? comments.length - start : start]

    // Skip this commit if the slot is not available.
    if (!slot) {
      continue
    }

    // Get the last commit group in the current slot, if any.
    const lastGroup = slot.at(-1)

    // If the last group has the same title as the current commit, append the commit to that group.
    if (lastGroup?.title === title) {
      lastGroup.commits.push(commit)
    } else {
      // Otherwise, create a new commit group.
      slot.push({title, commits: [commit]})
    }
  }

  const sharedPredecessorComments = new Set<number>()
  let hasPrecedingCommit = false

  // find the comments that share the same chronological predecessor commit
  for (const [index] of chronologicalComments.entries()) {
    const slotIndex = sort === CommentsSort.NewestFirst ? comments.length - index : index

    // Check if the current slot has any commit groups.
    if (slots[slotIndex]?.length) {
      // there is a preceding commit
      hasPrecedingCommit = true
    } else if (hasPrecedingCommit) {
      //  this comment shares the same chronological predecessor as the preceding commit
      sharedPredecessorComments.add(sort === CommentsSort.NewestFirst ? comments.length - index - 1 : index)
    }
  }

  const items: Array<ConnectedConversationComment | ConversationCommitGroup> = []

  // Iterate over each slot and interleave commit groups with comments.
  for (const [index, groups] of slots.entries()) {
    if (groups.length > 0) {
      // Determine if this commit group precedes a comment and should have its first or last commit marked as visible.
      const precedesComment = sort === CommentsSort.NewestFirst ? index > 0 : index < comments.length

      // Get the commit that should be marked as visible based on the sort order.
      const precedingCommit = sort === CommentsSort.NewestFirst ? groups[0]?.commits[0] : groups.at(-1)?.commits.at(-1)

      // Mark the preceding commit as visible if it precedes a comment.
      if (precedesComment && precedingCommit) {
        visibleCommitOids.add(precedingCommit.oid)
      }

      items.push({
        type: 'commits',
        key: `commits-${sort}-${groups[0]?.commits[0]?.oid}-${groups.at(-1)?.commits.at(-1)?.oid}`,
        commitGroups: groups,
        connection: precedesComment ? (sort === CommentsSort.NewestFirst ? 'above' : 'below') : undefined,
        // Include only the commits that are marked as visible.
        visibleCommitOids: groups.flatMap(group =>
          group.commits
            .filter(commit =>
              precedesComment ? commit.oid === precedingCommit?.oid : visibleCommitOids.has(commit.oid),
            )
            .map(commit => commit.oid),
        ),
      })
    }

    // Add the corresponding comment after the commit groups, if it exists.
    const comment = comments[index]

    // Add the comment to the items array if it exists.
    if (comment) {
      items.push({
        ...comment,
        key: commentItemKey(comment),
        connection: sharedPredecessorComments.has(index)
          ? sort === CommentsSort.NewestFirst
            ? 'below'
            : 'above'
          : undefined,
      })
    }
  }

  return items
}
